/**
 * Recipient portal — `/me`
 * - Unauthenticated: email-entry → "check your inbox" message.
 * - With ?token=<magic>: exchanges for a session token, then shows portal.
 * - Authenticated (session in sessionStorage): badge wall + handle + actions.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useFormatDate } from '../../hooks/useFormatDate';

const API_HOST = (() => {
  if (typeof window === 'undefined') return 'https://api.badges.ninja';
  return window.location.hostname.includes('staging')
    ? 'https://api-staging.badges.ninja'
    : 'https://api.badges.ninja';
})();

const SESSION_KEY = 'recipient_session';

interface AwardLite {
  id: string;
  image: string;
  issuedOn: string;
  expires?: string;
  badge: { name: string; issuer: { name: string } };
  recipientEmail?: string | null;
}

interface MeResponse {
  email: string;
  handle: string;
  displayName: string | null;
  publicProfileUrl: string;
  hiddenAwardIds: string[];
  awards: { count: number; items: AwardLite[]; lastEvaluatedKey?: string };
}

function readSession(): { token: string; email: string } | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function awardGuid(award: AwardLite): string {
  return award.id.split('/').pop() || award.id;
}

export function RecipientPortal() {
  const [params, setParams] = useSearchParams();
  const [session, setSession] = useState(readSession());
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  // Stage 1: token in URL → exchange for session
  useEffect(() => {
    const token = params.get('token');
    if (!token) return;
    setVerifying(true);
    fetch(`${API_HOST}/me/auth/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(r => r.json().then(b => ({ ok: r.ok, body: b })))
      .then(({ ok, body }) => {
        if (!ok) throw new Error(body?.error || 'Sign-in failed');
        const newSession = { token: body.token, email: body.email };
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(newSession));
        setSession(newSession);
        const next = new URLSearchParams(params); next.delete('token');
        setParams(next, { replace: true });
      })
      .catch(e => setVerifyError(e.message))
      .finally(() => setVerifying(false));
  }, [params, setParams]);

  if (verifying) return <CenteredSpinner />;
  if (verifyError) return <SignInForm initialError={verifyError} />;
  if (!session) return <SignInForm />;

  return <PortalView session={session} onSignOut={() => { sessionStorage.removeItem(SESSION_KEY); setSession(null); }} />;
}

function SignInForm({ initialError }: { initialError?: string } = {}) {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(initialError ?? null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const r = await fetch(`${API_HOST}/me/auth/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        throw new Error(b.error || 'Request failed');
      }
      setSent(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow p-8">
        <div className="text-center mb-6">
          <div className="mx-auto h-12 w-12 rounded-full bg-primary-50 border border-primary-100 flex items-center justify-center mb-3">
            <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900">{t('recipient.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('recipient.subtitle')}</p>
        </div>
        {sent ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 text-center">
            <Trans
              t={t}
              i18nKey="recipient.checkInbox"
              values={{ email }}
              components={{ s: <span className="font-medium" /> }}
            >
              Check your inbox at <span className="font-medium">{'{{email}}'}</span> — we sent you a sign-in link valid for 24 hours.
            </Trans>
            <p className="text-xs mt-1 text-emerald-600">{t('recipient.checkInboxHint')}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('recipient.yourEmail')}</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t('recipient.emailPlaceholder')}
                required
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            </div>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={submitting || !email.trim()}
              className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? t('recipient.sending') : t('recipient.sendLink')}
            </button>
          </form>
        )}
        <p className="mt-6 text-[11px] text-gray-400 text-center">
          {t('recipient.noPasswordHint')}
        </p>
      </div>
    </div>
  );
}

function PortalView({ session, onSignOut }: { session: { token: string; email: string }; onSignOut: () => void }) {
  const { t } = useTranslation('common');
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingHandle, setEditingHandle] = useState(false);
  const [handleDraft, setHandleDraft] = useState('');
  const [savingHandle, setSavingHandle] = useState(false);
  const [handleError, setHandleError] = useState<string | null>(null);
  const formatDate = useFormatDate();
  const hidden = useMemo(() => new Set(me?.hiddenAwardIds ?? []), [me]);

  useEffect(() => {
    fetch(`${API_HOST}/me`, { headers: { Authorization: `Bearer ${session.token}` } })
      .then(r => r.json().then(b => ({ ok: r.ok, body: b })))
      .then(({ ok, body }) => { if (!ok) throw new Error(body.error || 'Failed to load'); setMe(body); })
      .catch(e => {
        if ((e as Error).message?.includes('not authenticated')) onSignOut();
        else setError((e as Error).message);
      })
      .finally(() => setLoading(false));
  }, [session.token, onSignOut]);

  async function saveHandle() {
    setSavingHandle(true); setHandleError(null);
    try {
      const r = await fetch(`${API_HOST}/me/handle`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.token}` },
        body: JSON.stringify({ handle: handleDraft.trim().toLowerCase() }),
      });
      const b = await r.json();
      if (!r.ok) throw new Error(b.error || 'Failed');
      setMe(prev => prev ? { ...prev, handle: b.handle, publicProfileUrl: prev.publicProfileUrl.replace(prev.handle, b.handle) } : prev);
      setEditingHandle(false);
    } catch (e) { setHandleError((e as Error).message); }
    finally { setSavingHandle(false); }
  }

  async function toggleHidden(award: AwardLite, hide: boolean) {
    const guid = awardGuid(award);
    setMe(prev => prev ? { ...prev, hiddenAwardIds: hide ? [...prev.hiddenAwardIds, guid] : prev.hiddenAwardIds.filter(x => x !== guid) } : prev);
    fetch(`${API_HOST}/me/awards/${guid}/visibility`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.token}` },
      body: JSON.stringify({ hidden: hide }),
    }).catch(() => { /* optimistic update; tolerate failure */ });
  }

  if (loading) return <CenteredSpinner />;
  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center text-center p-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">{t('recipient.loadFailed')}</h1>
        <p className="text-sm text-gray-500 mb-4">{error}</p>
        <button onClick={onSignOut} className="text-sm text-primary-600 hover:underline">{t('recipient.signInAgain')}</button>
      </div>
    </div>
  );
  if (!me) return null;

  const awards = me.awards.items;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('recipient.title')}</h1>
            <p className="text-sm text-gray-500">{t('recipient.signedInAs', { email: me.email })}</p>
          </div>
          <button onClick={onSignOut} className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">
            {t('recipient.signOut')}
          </button>
        </div>

        {/* Profile card */}
        <div className="bg-white rounded-2xl shadow p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-400">{t('recipient.publicProfile')}</div>
              {editingHandle ? (
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-sm text-gray-500">badges.ninja/u/</span>
                  <input
                    type="text"
                    value={handleDraft}
                    onChange={e => setHandleDraft(e.target.value.toLowerCase())}
                    placeholder={t('recipient.handlePlaceholder')}
                    pattern="[a-z0-9_-]{3,20}"
                    className="rounded border border-gray-200 px-2 py-1 text-sm w-48"
                  />
                  <button onClick={saveHandle} disabled={savingHandle} className="rounded bg-primary-600 px-3 py-1 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-50">
                    {savingHandle ? t('recipient.saving') : t('recipient.save')}
                  </button>
                  <button onClick={() => { setEditingHandle(false); setHandleError(null); }} className="text-xs text-gray-500 hover:underline">{t('recipient.cancel')}</button>
                </div>
              ) : (
                <div className="mt-1 flex items-center gap-2">
                  <Link to={`/u/${me.handle}`} className="text-base font-semibold text-primary-700 hover:underline">badges.ninja/u/{me.handle}</Link>
                  <button onClick={() => { setHandleDraft(me.handle); setEditingHandle(true); }} className="text-xs text-gray-500 hover:underline">{t('recipient.change')}</button>
                </div>
              )}
              {handleError && <p className="text-xs text-red-600 mt-1">{handleError}</p>}
            </div>
            <a href={me.publicProfileUrl} target="_blank" rel="noopener noreferrer"
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50">
              {t('recipient.openProfile')} ↗
            </a>
          </div>
        </div>

        {/* Awards grid */}
        {awards.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center text-sm text-gray-500">
            {t('recipient.noBadgesYet', { email: me.email })}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {awards.map(a => {
              const isHidden = hidden.has(awardGuid(a));
              return (
                <div key={a.id} className={`rounded-2xl border bg-white shadow-card overflow-hidden ${isHidden ? 'opacity-60' : ''}`}>
                  <Link to={`/awards/${awardGuid(a)}`} className="block">
                    <div className="aspect-square bg-gray-50 flex items-center justify-center p-4">
                      <img src={a.image} alt={a.badge.name} className="max-h-full max-w-full object-contain" />
                    </div>
                  </Link>
                  <div className="p-3 border-t border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">{a.badge.name}</h3>
                    <p className="text-xs text-gray-500 truncate mt-0.5">{a.badge.issuer.name}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">{formatDate(a.issuedOn)}</p>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <Link to={`/awards/${awardGuid(a)}`} className="text-[11px] font-medium text-primary-600 hover:underline">{t('recipient.open')}</Link>
                      <button onClick={() => toggleHidden(a, !isHidden)} className="text-[11px] text-gray-500 hover:text-gray-700">
                        {isHidden ? t('recipient.showOnProfile') : t('recipient.hideFromProfile')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-8 text-[11px] text-gray-400 text-center">
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm(t('recipient.removeDataConfirm', 'Remove your data? This unbinds your email from every credential and deletes your public profile. This cannot be undone.'))) return;
              try {
                await fetch(`${API_HOST}/me/auth/forget`, { method: 'POST', headers: { Authorization: `Bearer ${session.token}` } });
              } catch { /* best effort */ }
              onSignOut();
            }}
            className="hover:underline"
          >{t('recipient.removeData')} →</button>
        </p>
      </div>
    </div>
  );
}

function CenteredSpinner() {
  return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="h-8 w-8 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
    </div>
  );
}
